import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrashIcon, MinusIcon, PlusIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useCart } from '../context/CartContext';
import StockErrorModal from '../components/StockErrorModal';
import { facebookPixel } from '../utils/facebookPixel';

const Cart: React.FC = () => {
  const { items: cartItems, updateQuantity, removeItem, subtotal, stockError, clearStockError } = useCart();
  const [isUpdating, setIsUpdating] = useState<number | null>(null);
  const [showToast, setShowToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set(cartItems.map(item => item.key)));

  const handleUpdateQuantity = async (key: string, newQuantity: number) => {
    setIsUpdating(1); // simple spinner toggle; not per-item id anymore
    await new Promise(resolve => setTimeout(resolve, 150));
    updateQuantity(key, newQuantity);
    setIsUpdating(null);
    showToastMessage('Cart updated successfully', 'success');
  };

  const handleRemove = (key: string) => {
    const item = cartItems.find(i => i.key === key);
    removeItem(key);
    if (item) showToastMessage(`${item.name} removed from cart`, 'success');
  };

  // const saveForLater = (key: string) => {
  //   const item = cartItems.find(i => i.key === key);
  //   if (!item) return;
  //   setSavedItems(prev => [...prev, item]);
  //   removeItem(key);
  //   showToastMessage(`${item.name} saved for later`, 'success');
  // };

  // const moveToCart = (key: string) => {
  //   const item = savedItems.find(i => i.key === key);
  //   if (!item) return;
  //   // Re-add by updating quantity on existing key
  //   updateQuantity(item.key, item.quantity);
  //   setSavedItems(items => items.filter(i => i.key !== key));
  //   showToastMessage(`${item.name} moved to cart`, 'success');
  // };

  const showToastMessage = (message: string, type: 'success' | 'error') => {
    setShowToast({ message, type });
    setTimeout(() => setShowToast(null), 3000);
  };

  const handleItemSelect = (key: string, selected: boolean) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(key);
      } else {
        newSet.delete(key);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    setSelectedItems(new Set(cartItems.map(item => item.key)));
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
  };

  // Calculate totals for selected items only
  const selectedCartItems = cartItems.filter(item => selectedItems.has(item.key));
  const selectedSubtotal = selectedCartItems.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);

  useEffect(() => {
    // Auto-hide toast after 3 seconds
    if (showToast) {
      const timer = setTimeout(() => setShowToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  // Update selected items when cart items change
  useEffect(() => {
    setSelectedItems(prev => {
      const newSet = new Set<string>();
      cartItems.forEach(item => {
        if (prev.has(item.key)) {
          newSet.add(item.key);
        }
      });
      // Auto-select new items
      if (newSet.size === 0 && cartItems.length > 0) {
        return new Set(cartItems.map(item => item.key));
      }
      return newSet;
    });
  }, [cartItems]);

  // Use selected items for calculations
  const total = selectedSubtotal;

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center py-24">
            {/* Shopping bag illustration */}
            <div className="relative mb-8">
              <div className="w-32 h-32 bg-gray-100 rounded-full flex items-center justify-center">
                <svg className="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
              <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                <span className="text-gray-400 text-lg font-bold">0</span>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-3">Your cart is empty</h2>
            <p className="text-gray-500 mb-2 text-center max-w-sm">
              Looks like you haven't added anything to your cart yet.
            </p>
            <p className="text-gray-400 text-sm mb-8">Browse our collection and find something you love.</p>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/product-signature"
                className="inline-flex items-center justify-center bg-gray-900 text-white px-8 py-3 rounded-lg hover:bg-gray-800 transition-all duration-200 font-semibold shadow-sm hover:shadow-md"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                Start Shopping
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center border border-gray-300 text-gray-700 px-8 py-3 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16 pb-8">
      {/* Stock Error Modal */}
      <StockErrorModal
        isOpen={!!stockError}
        onClose={clearStockError}
        message={stockError?.message || ''}
        productName={stockError?.productName}
        availableStock={stockError?.availableStock}
      />
      
      {/* Toast Notification */}
      {showToast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center p-4 rounded-lg shadow-lg transition-all duration-300 ease-in-out ${
          showToast.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          <CheckCircleIcon className={`h-5 w-5 mr-2 ${
            showToast.type === 'success' ? 'text-green-500' : 'text-red-500'
          }`} />
          <span className="font-medium">{showToast.message}</span>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Shopping Cart</h1>
          <div className="flex items-center text-sm text-gray-500">
            <ShieldCheckIcon className="h-4 w-4 mr-1" />
            Secure Checkout
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6">
            {cartItems.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Cart Items ({cartItems.length})
                    </h2>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={handleSelectAll}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200"
                      >
                        Select All
                      </button>
                      <button
                        onClick={handleDeselectAll}
                        className="text-sm text-gray-600 hover:text-gray-800 font-medium transition-colors duration-200"
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                </div>

                <div className="divide-y divide-gray-100">
                  {cartItems.map((item) => (
                    <div key={item.key} className={`p-4 md:p-6 transition-colors duration-200 ${
                      selectedItems.has(item.key) ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'
                    }`}>
                      {/* Mobile Layout */}
                      <div className="md:hidden">
                        <div className="flex items-start space-x-3 mb-3">
                          {/* Selection Checkbox */}
                          <div className="flex items-center pt-1">
                            <input
                              type="checkbox"
                              checked={selectedItems.has(item.key)}
                              onChange={(e) => handleItemSelect(item.key, e.target.checked)}
                              className="w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            />
                          </div>
                          
                          {/* Image */}
                          <div className="relative flex-shrink-0">
                            <img
                              src={item.image || 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=200&h=200&fit=crop'}
                              alt={item.name}
                              className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                            />
                            {item.compareAtPrice && (
                              <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full font-semibold">
                                {Math.round(((item.compareAtPrice - item.unitPrice) / item.compareAtPrice) * 100)}%
                              </div>
                            )}
                          </div>
                          
                          {/* Product Info */}
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-semibold text-gray-900 mb-1 line-clamp-2">
                              {item.name}
                            </h3>
                            <div className="flex flex-col gap-1 text-xs text-gray-600 mb-2">
                              {item.sizeName && <span>Size: {item.sizeName}</span>}
                              {item.colorName && (
                                <span className="flex items-center gap-1">
                                  Color: {item.colorName}
                                  {item.colorCode && (
                                    <span
                                      className="inline-block w-3 h-3 rounded-full border border-gray-300"
                                      style={{ backgroundColor: item.colorCode }}
                                    />
                                  )}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-base font-bold text-gray-900">
                                {item.unitPrice.toLocaleString("en-US")} Ks
                              </span>
                              {item.compareAtPrice && (
                                <span className="text-xs text-gray-500 line-through">
                                  {item.compareAtPrice.toLocaleString("en-US")}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Quantity and Actions Row */}
                        <div className="flex items-center justify-between pl-7">
                          <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden bg-white">
                            <button
                              onClick={() => handleUpdateQuantity(item.key, Math.max(1, item.quantity - 1))}
                              disabled={item.quantity <= 1 || isUpdating !== null}
                              className="px-2 py-1 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <MinusIcon className="h-4 w-4 text-gray-600" />
                            </button>
                            <span className="px-3 py-1 text-sm font-medium text-gray-900 min-w-[2rem] text-center">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => handleUpdateQuantity(item.key, item.quantity + 1)}
                              disabled={isUpdating !== null}
                              className="px-2 py-1 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <PlusIcon className="h-4 w-4 text-gray-600" />
                            </button>
                          </div>
                          
                          <button
                            onClick={() => handleRemove(item.key)}
                            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                        
                        <div className="mt-2 pl-7 text-xs text-green-600 flex items-center">
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          {item.inStock ? 'In Stock' : 'Out of Stock'}
                        </div>
                      </div>

                      {/* Desktop Layout */}
                      <div className="hidden md:flex items-start space-x-4">
                        {/* Selection Checkbox */}
                        <div className="flex items-center pt-2">
                          <input
                            type="checkbox"
                            checked={selectedItems.has(item.key)}
                            onChange={(e) => handleItemSelect(item.key, e.target.checked)}
                            className="w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2 transition-all duration-200 ease-in-out"
                          />
                        </div>
                        <div className="relative group">
                          <img
                            src={item.image || 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=200&h=200&fit=crop'}
                            alt={item.name}
                            className="w-24 h-24 object-cover rounded-lg border border-gray-200 group-hover:shadow-md transition-shadow duration-200"
                          />
                          {item.compareAtPrice && (
                            <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
                              {Math.round(((item.compareAtPrice - item.unitPrice) / item.compareAtPrice) * 100)}% OFF
                            </div>
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            {item.name}
                          </h3>
                          
                          {/* Product Details */}
                          <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-2">
                            {item.sizeName && <span>Size: {item.sizeName}</span>}
                            {item.colorName && (
                              <span className="flex items-center gap-1">
                                Color: {item.colorName}
                                {item.colorCode && (
                                  <span
                                    className="inline-block w-3 h-3 rounded-full border border-gray-300 ml-1"
                                    style={{ backgroundColor: item.colorCode }}
                                    title={item.colorName}
                                  />
                                )}
                              </span>
                            )}
                          </div>
                          
                          {/* Price */}
                          <div className="flex items-center space-x-2 mb-3">
                            <span className="text-lg font-bold text-gray-900">
                              {item.unitPrice.toLocaleString("en-US")} MMK
                            </span>
                            {item.compareAtPrice && (
                              <span className="text-sm text-gray-500 line-through ml-2">
                                {item.compareAtPrice.toLocaleString("en-US")} MMK
                              </span>
                            )}
                          </div>
                          
                          {/* Stock Status & Delivery */}
                          <div className="flex items-center space-x-4 text-sm">
                            <span className={`flex items-center ${
                              item.inStock ? 'text-green-600' : 'text-red-600'
                            }`}>
                              <CheckCircleIcon className="h-4 w-4 mr-1" />
                              {item.inStock ? 'In Stock' : 'Out of Stock'}
                            </span>
                          </div>
                        </div>

                        {/* Quantity & Actions */}
                        <div className="flex flex-col items-end space-y-3">
                          {/* Total Price */}
                          <div className="text-right">
                            <p className="text-lg font-bold text-gray-900">
                              {(item.unitPrice * item.quantity).toLocaleString("en-US")} MMK
                            </p>
                          </div>
                          
                          {/* Quantity Controls */}
                          <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden bg-white">
                            <button
                              onClick={() => handleUpdateQuantity(item.key, Math.max(1, item.quantity - 1))}
                              disabled={isUpdating !== null || item.quantity <= 1}
                              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 transition-all duration-200 ease-in-out disabled:opacity-50"
                            >
                              <MinusIcon className="h-4 w-4" />
                            </button>
                            <span className="px-4 py-2 border-x border-gray-300 min-w-[3rem] text-center font-medium bg-gray-50">
                              {isUpdating !== null ? (
                                <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-gray-600 rounded-full mx-auto"></div>
                              ) : (
                                item.quantity
                              )}
                            </span>
                            <button
                              onClick={() => {
                                const max = item.stockQuantity && item.stockQuantity > 0 ? item.stockQuantity : Number.POSITIVE_INFINITY;
                                const next = Math.min(item.quantity + 1, max);
                                handleUpdateQuantity(item.key, next);
                              }}
                              disabled={
                                isUpdating !== null || (item.stockQuantity && item.stockQuantity > 0 ? item.quantity >= item.stockQuantity : false)
                              }
                              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 transition-all duration-200 ease-in-out disabled:opacity-50"
                            >
                              <PlusIcon className="h-4 w-4" />
                            </button>
                          </div>
                          
                          {/* Action Buttons */}
                          <button
                            onClick={() => handleRemove(item.key)}
                            className="flex items-center px-3 py-1.5 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-all duration-200 ease-in-out"
                          >
                            <TrashIcon className="h-4 w-4 mr-1" />
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="mt-6">
              <Link
                to="/product-signature"
                className="inline-flex items-center text-gray-600 hover:text-gray-800 font-medium transition-colors duration-200"
              >
                ← Continue Shopping
              </Link>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sticky top-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                Order Summary
              </h2>

              {/* Progress Bar for Free Shipping */}
              {subtotal < 50 && (
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-800">
                      Free shipping at $50
                    </span>
                    <span className="text-sm font-semibold text-blue-800">
                      ${(50 - subtotal).toFixed(2)} to go
                    </span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-in-out"
                      style={{ width: `${Math.min((subtotal / 50) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
              )}

              <div className="space-y-4 mb-6">
                {/* Individual Item Breakdown */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                    <h3 className="text-sm font-medium text-gray-900">Selected Items ({selectedItems.size})</h3>
                    {selectedItems.size !== cartItems.length && (
                      <span className="text-xs text-gray-500">
                        {cartItems.length - selectedItems.size} items not selected
                      </span>
                    )}
                  </div>
                  {selectedCartItems.map((item) => (
                    <div key={item.key} className="flex justify-between items-start text-sm">
                      <div className="flex-1 pr-2">
                        <p className="text-gray-900 font-medium truncate">{item.name}</p>
                        <div className="text-gray-500 text-xs mt-1">
                          {item.sizeName && <span>Size: {item.sizeName}</span>}
                          {item.sizeName && item.colorName && <span> • </span>}
                          {item.colorName && (
                            <span className="inline-flex items-center gap-1">
                              Color: {item.colorName}
                              {item.colorCode && (
                                <span
                                  className="inline-block w-2 h-2 rounded-full border border-gray-300"
                                  style={{ backgroundColor: item.colorCode }}
                                />
                              )}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-600 text-xs mt-1">
                          {item.unitPrice.toLocaleString("en-US")} MMK × {item.quantity}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">
                          {(item.unitPrice * item.quantity).toLocaleString("en-US")} MMK
                        </p>
                        {item.compareAtPrice && (
                          <p className="text-xs text-green-600">
                            Save {((item.compareAtPrice - item.unitPrice) * item.quantity).toLocaleString("en-US")} MMK
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-200 pt-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Subtotal ({selectedItems.size} items)</span>
                    <span className="font-semibold text-gray-900">{selectedSubtotal.toLocaleString("en-US")} MMK</span>
                  </div>
                  
                  {/* Savings Display */}
                  {selectedCartItems.some(item => item.compareAtPrice) && (
                    <div className="flex justify-between items-center text-green-600">
                      <span className="font-medium">You Save</span>
                      <span className="font-semibold">
                        -{selectedCartItems.reduce((savings, item) => 
                          savings + (item.compareAtPrice ? (item.compareAtPrice - item.unitPrice) * item.quantity : 0), 0
                        ).toLocaleString("en-US")} MMK
                      </span>
                    </div>
                  )}
                  
                  <div className="border-t border-gray-200 pt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-900">Total</span>
                      <span className="text-lg font-bold text-gray-900">
                        {total.toLocaleString("en-US")} MMK
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Estimated Delivery removed per request */}

              <Link
                to="/checkout"
                state={{ selectedItems: Array.from(selectedItems) }}
                className={`block w-full py-4 px-6 rounded-lg transition-all duration-200 ease-in-out font-semibold text-lg mb-4 text-center ${
                  selectedItems.size > 0
                    ? 'bg-gray-900 text-white hover:bg-gray-800 hover:shadow-lg active:scale-[0.998]'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                onClick={(e) => {
                  if (selectedItems.size === 0) {
                    e.preventDefault();
                    showToastMessage('Please select at least one item to checkout', 'error');
                  } else {
                    // Track Facebook Pixel InitiateCheckout event
                    const selectedCartItems = cartItems.filter(item => selectedItems.has(item.key));
                    const totalValue = selectedCartItems.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
                    facebookPixel.trackInitiateCheckout({
                      content_ids: selectedCartItems.map(item => item.productId.toString()),
                      content_type: 'product',
                      value: totalValue,
                      currency: 'MMK',
                      num_items: selectedCartItems.reduce((sum, item) => sum + item.quantity, 0)
                    });
                  }
                }}
              >
                Proceed to Checkout ({selectedItems.size} items)
              </Link>
              {/* Payment methods and SSL note removed per request */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
